/**
 * AI先輩の利用状況（issue #109・docs/01 11-9）。
 *
 * **個人が誰に何を聞いたかは出ません。** 出るのは月ごとの人数と件数だけで、
 * 繊細な領域（労務・健康など）の質問は件数にだけ含めます（内訳は出しません）。
 */
import { useEffect, useState, type ReactNode } from 'react';
import { useApi } from '../api/context.js';
import type { AiUsageResponse } from '../api/types.js';
import { EmptyBlock, ErrorBlock, LoadingBlock } from '../components/StatusBlock.js';
import { loadFailure, type LoadFailure } from '../api/load-failure.js';
import { Card } from '../components/ui/Card.js';
import { DataTable } from '../components/ui/DataTable.js';
import { PageHeader } from '../components/ui/PageHeader.js';

export function AiUsagePage(): ReactNode {
  const api = useApi();
  const [data, setData] = useState<AiUsageResponse | null>(null);
  const [error, setError] = useState<LoadFailure | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setData(null);
    setError(null);
    api
      .get<AiUsageResponse>('/admin/ai/usage')
      .then((loaded) => {
        if (!cancelled) setData(loaded);
      })
      .catch((caught: unknown) => {
        if (!cancelled) {
          setError(loadFailure(caught));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [api, reloadKey]);

  return (
    <article>
      <PageHeader
        title="AI先輩の利用状況"
        description="月ごとの人数と件数です。どなたが何を聞いたかは出ません。拠点を管理する方には、所属拠点と受け持つ拠点の方の分だけを数えます。"
      />

      {error ? <ErrorBlock failure={error} onRetry={() => setReloadKey((n) => n + 1)} /> : null}
      {!error && !data ? <LoadingBlock label="利用状況を読み込んでいます…" /> : null}
      {data && data.months.length === 0 ? (
        <EmptyBlock label="まだ利用の記録がありません。" />
      ) : null}

      {data && data.months.length > 0 ? (
        <DataTable label="月ごとの利用状況">
          <thead>
            <tr>
              <th scope="col">月</th>
              <th scope="col">使った人数</th>
              <th scope="col">質問</th>
              <th scope="col">答えられた</th>
              <th scope="col" className="ui-cell-soft">
                担当の方へ案内した
              </th>
              <th scope="col" className="ui-cell-soft">
                答えられなかった
              </th>
              <th scope="col" className="ui-cell-soft">
                繊細な質問（労務・健康など）
              </th>
              <th scope="col" className="ui-cell-soft">
                「AI先輩に教える」へのメッセージ
              </th>
            </tr>
          </thead>
          <tbody>
            {data.months.map((month) => (
              <tr key={month.month}>
                <th scope="row">{monthLabel(month.month)}</th>
                <td className="ui-cell-num">{month.people}名</td>
                <td className="ui-cell-num">{month.asked}件</td>
                <td className="ui-cell-num">{month.answered}件</td>
                <td className="ui-cell-num">{month.guided}件</td>
                <td className="ui-cell-num">{month.unanswered}件</td>
                <td className="ui-cell-num">{month.sensitive}件</td>
                <td className="ui-cell-num">{month.taught}件</td>
              </tr>
            ))}
          </tbody>
        </DataTable>
      ) : null}

      {data && data.top_questions.length > 0 ? (
        <Card
          title="よく聞かれた質問"
          description="労務・健康など繊細な質問は入りません（件数だけを数えます）。"
        >
          <ol className="admin-ai-top">
            {data.top_questions.map((row) => (
              <li key={row.question}>
                {row.question}
                <small>{row.count}件</small>
              </li>
            ))}
          </ol>
        </Card>
      ) : null}
    </article>
  );
}

/** 「2026-09-01」→「2026年9月」。 */
function monthLabel(month: string): string {
  const parts = month.split('-');
  const year = parts[0];
  const monthNumber = parts[1];
  if (!year || !monthNumber) return month;
  return `${year}年${Number(monthNumber)}月`;
}
